<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import BusinessDay from '../inbox/components/BusinessDay.vue';
import {
  defaultTimeSlot,
  timeSlotParse,
  timeSlotTransform,
  timeZoneOptions,
} from '../inbox/helpers/businessHour';

const props = defineProps({
  team: {
    type: Object,
    required: true,
  },
  embedded: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const teams = useMapGetter('teams/getTeams');

const dayNames = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

const routingEnabled = ref(false);
const routingTimezone = ref('UTC');
const routingTargetTeamId = ref(null);
const timeSlots = ref(defaultTimeSlot.map(slot => ({ ...slot })));
const isSaving = ref(false);

const timeZones = timeZoneOptions();
const teamOptions = computed(() =>
  teams.value
    .filter(team => team.id !== props.team.id)
    .map(team => ({ label: team.name, value: team.id }))
);
const hasError = computed(
  () =>
    routingEnabled.value &&
    timeSlots.value.some(slot => slot.from && !slot.valid)
);

const setDefaults = team => {
  routingEnabled.value = Boolean(team.routing_enabled);
  routingTimezone.value = team.routing_timezone || 'UTC';
  routingTargetTeamId.value = team.routing_target_team_id || null;

  const parsedSchedule = timeSlotParse(team.routing_schedule || []);
  timeSlots.value = parsedSchedule.length
    ? parsedSchedule
    : defaultTimeSlot.map(slot => ({ ...slot }));
};

const onSlotUpdate = (slotIndex, slotData) => {
  timeSlots.value = timeSlots.value.map(slot =>
    slot.day === slotIndex ? slotData : slot
  );
};

const saveRoutingSettings = async () => {
  const invalidSettings =
    hasError.value || (routingEnabled.value && !routingTargetTeamId.value);
  if (invalidSettings) return;

  isSaving.value = true;
  try {
    await store.dispatch('teams/update', {
      id: props.team.id,
      routing_enabled: routingEnabled.value,
      routing_timezone: routingTimezone.value,
      routing_target_team_id: routingTargetTeamId.value,
      routing_schedule: timeSlotTransform(timeSlots.value),
    });
    useAlert(t('TEAMS_SETTINGS.ROUTING.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('TEAMS_SETTINGS.ROUTING.API.ERROR_MESSAGE')
    );
  } finally {
    isSaving.value = false;
  }
};

watch(() => props.team, setDefaults, { immediate: true });
</script>

<template>
  <div :class="{ 'mt-8 border-t border-n-weak pt-8': !embedded }">
    <SettingsToggleSection
      v-model="routingEnabled"
      :header="$t('TEAMS_SETTINGS.ROUTING.TOGGLE_LABEL')"
      :description="$t('TEAMS_SETTINGS.ROUTING.TOGGLE_HELP')"
    />

    <div v-if="routingEnabled" class="mt-6 flex flex-col gap-6">
      <SettingsFieldSection
        :label="$t('TEAMS_SETTINGS.ROUTING.TARGET_TEAM_LABEL')"
      >
        <ComboBox
          v-model="routingTargetTeamId"
          :options="teamOptions"
          :placeholder="$t('TEAMS_SETTINGS.ROUTING.TARGET_TEAM_PLACEHOLDER')"
          class="[&>div>button]:!bg-n-alpha-black2"
        />
      </SettingsFieldSection>

      <SettingsFieldSection
        :label="$t('TEAMS_SETTINGS.ROUTING.TIMEZONE_LABEL')"
      >
        <ComboBox
          v-model="routingTimezone"
          :options="timeZones"
          :placeholder="$t('TEAMS_SETTINGS.ROUTING.TIMEZONE_PLACEHOLDER')"
          class="[&>div>button]:!bg-n-alpha-black2"
        />
      </SettingsFieldSection>

      <div>
        <p class="mb-3 text-body-main text-n-slate-12 font-medium">
          {{ $t('TEAMS_SETTINGS.ROUTING.WEEKLY_TITLE') }}
        </p>
        <div class="w-full overflow-x-auto">
          <table
            class="min-w-full table-auto outline outline-1 -outline-offset-1 outline-n-weak rounded-xl"
          >
            <thead>
              <tr class="border-b border-n-weak">
                <th class="py-3 px-4 text-start text-heading-3 text-n-slate-12">
                  {{ $t('TEAMS_SETTINGS.ROUTING.DAY') }}
                </th>
                <th class="py-3 px-3 text-start text-heading-3 text-n-slate-12">
                  {{ $t('TEAMS_SETTINGS.ROUTING.AVAILABILITY') }}
                </th>
                <th class="py-3 px-3 text-start text-heading-3 text-n-slate-12">
                  {{ $t('TEAMS_SETTINGS.ROUTING.HOURS') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <BusinessDay
                v-for="timeSlot in timeSlots"
                :key="timeSlot.day"
                :day-name="dayNames[timeSlot.day]"
                :time-slot="timeSlot"
                @update="data => onSlotUpdate(timeSlot.day, data)"
              />
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="flex justify-end mt-6">
      <NextButton
        :label="$t('TEAMS_SETTINGS.ROUTING.SAVE')"
        :is-loading="isSaving"
        :disabled="hasError || (routingEnabled && !routingTargetTeamId)"
        @click="saveRoutingSettings"
      />
    </div>
  </div>
</template>
